<template>
  <div
    ref="layoutEl"
    class="ca-banner__layout"
    @mousemove="onMouseMove"
    @mouseleave="onMouseLeave"
    @touchstart.passive="onTouchStart"
    @touchend="onTouchEnd"
    @mousedown.prevent="onDragStart"
    @mouseup="onDragEnd"
  >
    <slot :activeSlide="activeSlide" />

    <IconButton
      v-if="showSideArrow"
      ref="arrowLeftEl"
      class="ca-banner__arrow ca-banner__arrow--left opacity-0 pointer-events-none max-[1024px]:hidden shadow-2"
      :class="{ 'is-visible': arrowLeft }"
      type="inverted"
      variant="clear"
      size="xl"
      aria-label="Slide anterior"
      @clicked="prevSlide"
    >
      <template #trailingIcon>
        <IconComponent name="chev_left" size="2" class="text-white" />
      </template>
    </IconButton>

    <IconButton
      v-if="showSideArrow"
      ref="arrowRightEl"
      class="ca-banner__arrow ca-banner__arrow--right opacity-0 pointer-events-none max-[1024px]:hidden shadow-2"
      :class="{ 'is-visible': arrowRight }"
      type="inverted"
      variant="clear"
      size="xl"
      aria-label="Slide seguinte"
      @clicked="nextSlide"
    >
      <template #trailingIcon>
        <IconComponent name="chev_right" size="2" class="text-white" />
      </template>
    </IconButton>

    <div
      class="ca-banner__footer ca-bullet-container flex items-center pt-size-5 pb-size-11"
    >
      <slot
        name="footer"
        :is-playing="isPlaying"
        :dot-progress="dotProgress"
        :toggle-play="togglePlay"
        :go-to-slide="goToSlide"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { IconComponent, IconButton } from "ca-online-design-system";

defineOptions({ name: "BannerSlider" });

const props = defineProps({
  slideCount: {
    type: Number,
    required: true,
  },
  autoplayDelay: {
    type: Number,
    default: 7000,
  },
  showSideArrow: {
    type: Boolean,
    default: true,
  },
  activeSlide: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["update:activeSlide"]);

// --- Carousel state ---
const isPlaying = ref(true);
const dotProgress = ref(0);

// --- Arrow hover state ---
const layoutEl = ref(null);
const arrowLeftEl = ref(null);
const arrowRightEl = ref(null);
const arrowLeft = ref(false);
const arrowRight = ref(false);

// --- Mouse hover: reveal arrows near edges ---
function onMouseMove(e) {
  if (!layoutEl.value) return;
  const rect = layoutEl.value.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const w = rect.width;

  const leftEl = arrowLeftEl.value?.$el ?? arrowLeftEl.value;
  const rightEl = arrowRightEl.value?.$el ?? arrowRightEl.value;

  const leftZone = leftEl
    ? leftEl.getBoundingClientRect().right - rect.left + 16
    : w * 0.08;
  const rightZone = rightEl
    ? rightEl.getBoundingClientRect().left - rect.left - 16
    : w * 0.92;

  if (x < leftZone) {
    arrowLeft.value = true;
    arrowRight.value = false;
  } else if (x > rightZone) {
    arrowRight.value = true;
    arrowLeft.value = false;
  } else {
    arrowLeft.value = false;
    arrowRight.value = false;
  }
}

function onMouseLeave() {
  arrowLeft.value = false;
  arrowRight.value = false;
}

// --- Touch swipe ---
let touchStartX = 0;
let touchStartY = 0;

function onTouchStart(e) {
  touchStartX = e.touches[0].clientX;
  touchStartY = e.touches[0].clientY;
}

function onTouchEnd(e) {
  const dx = e.changedTouches[0].clientX - touchStartX;
  const dy = e.changedTouches[0].clientY - touchStartY;
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);

  if (absDx > 40 && absDx > absDy) {
    if (dx < 0) {
      nextSlide();
    } else {
      prevSlide();
    }
  }
}

// --- Mouse drag ---
let dragStartX = 0;

function onDragStart(e) {
  dragStartX = e.clientX;
}

function onDragEnd(e) {
  const dx = e.clientX - dragStartX;
  if (Math.abs(dx) > 40) {
    if (dx < 0) {
      nextSlide();
    } else {
      prevSlide();
    }
  }
}

// --- Navigation ---
function prevSlide() {
  emit(
    "update:activeSlide",
    (props.activeSlide - 1 + props.slideCount) % props.slideCount,
  );
  if (isPlaying.value) {
    stopProgress();
    startProgress();
  } else {
    dotProgress.value = 0;
  }
}

function nextSlide() {
  emit("update:activeSlide", (props.activeSlide + 1) % props.slideCount);
  if (isPlaying.value) {
    stopProgress();
    startProgress();
  } else {
    dotProgress.value = 0;
  }
}

// --- Autoplay progress ---
let animFrame = null;
let progressStart = 0;
let cycleToken = 0;

function startProgress() {
  const token = ++cycleToken;
  progressStart = performance.now();
  dotProgress.value = 0;

  function tick() {
    if (token !== cycleToken) return;
    const elapsed = performance.now() - progressStart;
    const progress = Math.min(elapsed / props.autoplayDelay, 1);
    dotProgress.value = progress * 100;

    if (progress < 1) {
      animFrame = requestAnimationFrame(tick);
    } else {
      emit("update:activeSlide", (props.activeSlide + 1) % props.slideCount);
      startProgress();
    }
  }

  animFrame = requestAnimationFrame(tick);
}

function stopProgress() {
  cycleToken++;
  if (animFrame) cancelAnimationFrame(animFrame);
  dotProgress.value = 0;
}

function togglePlay() {
  if (isPlaying.value) {
    isPlaying.value = false;
    stopProgress();
  } else {
    isPlaying.value = true;
    startProgress();
  }
}

function goToSlide(idx) {
  emit("update:activeSlide", idx);
  if (isPlaying.value) {
    stopProgress();
    startProgress();
  } else {
    dotProgress.value = 0;
  }
}

onMounted(() => {
  if (props.slideCount > 1) startProgress();
});

onUnmounted(() => {
  stopProgress();
});
</script>

<style scoped lang="scss">
.ca-banner__layout {
  position: relative;
  height: 100%;
  width: 100%;
}

.ca-banner__footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  pointer-events: none;

  :deep(*) {
    pointer-events: auto;
  }
}

.ca-banner__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  transition: opacity 0.25s ease;
  background-color: var(--ca-color-neutrals-inverted-20) !important;
  border: none !important;

  &:hover {
    background-color: var(--ca-color-neutrals-inverted-30) !important;
  }

  &:active {
    background-color: var(
      --ca-color-neutrals-inverted-40,
      rgba(255, 255, 255, 0.4)
    ) !important;
  }

  &.is-visible {
    opacity: 1;
    pointer-events: auto;
  }
}

.ca-banner__arrow--left {
  left: 22px;
}

.ca-banner__arrow--right {
  right: 22px;
}

@media (min-width: 1025px) {
  .ca-banner__footer {
    padding-top: 44px;
  }

  .ca-banner__layout {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: 1fr;
    align-items: center;
    padding-left: 32px;
    padding-right: 32px;
    box-sizing: border-box;
  }

  .ca-banner__arrow {
    position: relative;
    top: auto;
    transform: none;
    justify-self: center;
    grid-row: 1;
  }

  .ca-banner__arrow--left {
    left: auto;
    grid-column: 1;
    justify-self: start;
  }

  .ca-banner__arrow--right {
    right: auto;
    grid-column: 12;
    justify-self: end;
  }
}

@media (min-width: 1366px) {
  .ca-banner__layout {
    padding-left: 44px;
    padding-right: 44px;
  }
}

@media (min-width: 1536px) {
  .ca-banner__layout {
    padding-left: 128px;
    padding-right: 128px;
  }
}

@media (min-width: 1920px) {
  .ca-banner__layout {
    padding-left: 0;
    padding-right: 0;
    grid-template-columns: 1fr repeat(12, calc((1920px - 640px) / 12)) 1fr;
  }

  .ca-banner__arrow--left {
    grid-column: 2;
  }

  .ca-banner__arrow--right {
    grid-column: 13;
  }
}
@media (max-width: 1024px) {
  .ca-banner__layout {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .ca-banner__footer {
    position: relative;
    width: 100%;
    justify-content: space-between;
  }
}

@media (max-width: 767px) {
  .ca-banner__layout {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .ca-banner__footer {
    padding-top: 24px;
    padding-bottom: 56px;
  }

  .ca-banner__arrow {
    display: none;
  }
}
</style>
